import React from 'react';
import {
  Box, Grid, GridItem, FormControl, FormLabel, Input, Select, Textarea, Text,
} from '@chakra-ui/react';
import {
  TYPE_LABEL, KIND_LABEL, STATUS, STATUSES_WITH_REASON, CHANNEL_LABEL, PRIORITY, PAYMENT, DELIVERY_LABEL,
  toDateInput,
} from 'api/administrador/documentRequests';

/**
 * Campos de una solicitud, compartidos por «Nueva solicitud» y el detalle.
 * values: estado plano del formulario (ver emptyValues / valuesFromRequest).
 */

export const emptyValues = (defaultAmount) => ({
  type: 'constancia',
  constancyKind: 'principal',
  status: 'nueva',
  statusReason: '',
  priority: 'baja',
  channel: 'hibot',
  assignedTo: '',
  requestedAt: toDateInput(new Date()),
  deliveredAt: '',
  observations: '',
  phone: '',
  email: '',
  deliveryMethod: 'digital',
  deliveryAddress: '',
  deliveryNeighborhood: '',
  deliveryCity: '',
  deliveryDepartment: '',
  deliveryPhone: '',
  amount: defaultAmount ?? '',
  paymentStatus: 'pendiente',
  comments: '',
});

export const valuesFromRequest = (r) => ({
  type: r.type || 'constancia',
  constancyKind: r.constancyKind || '',
  status: r.status || 'nueva',
  statusReason: r.statusReason || '',
  priority: r.priority || 'baja',
  channel: r.channel || 'hibot',
  assignedTo: r.assignedTo?.id ? String(r.assignedTo.id) : '',
  requestedAt: toDateInput(r.requestedAt),
  deliveredAt: toDateInput(r.deliveredAt),
  observations: r.observations || '',
  phone: r.phone || '',
  email: r.email || '',
  deliveryMethod: r.deliveryMethod || 'digital',
  deliveryAddress: r.delivery?.address || '',
  deliveryNeighborhood: r.delivery?.neighborhood || '',
  deliveryCity: r.delivery?.city || '',
  deliveryDepartment: r.delivery?.department || '',
  deliveryPhone: r.delivery?.phone || '',
  amount: r.amount ?? '',
  paymentStatus: r.paymentStatus || 'pendiente',
  comments: r.comments || '',
});

/** Del formulario al cuerpo que espera la API. */
export const bodyFromValues = (v) => ({
  type: v.type,
  constancyKind: v.type === 'constancia' ? (v.constancyKind || null) : null,
  status: v.status,
  statusReason: v.statusReason,
  priority: v.priority,
  channel: v.channel,
  assignedTo: v.assignedTo || null,
  requestedAt: v.requestedAt || null,
  deliveredAt: v.deliveredAt || null,
  observations: v.observations,
  phone: v.phone,
  email: v.email,
  deliveryMethod: v.deliveryMethod,
  delivery: {
    address: v.deliveryAddress,
    neighborhood: v.deliveryNeighborhood,
    city: v.deliveryCity,
    department: v.deliveryDepartment,
    phone: v.deliveryPhone,
  },
  amount: v.amount === '' ? null : v.amount,
  paymentStatus: v.paymentStatus,
  comments: v.comments,
});

function SectionTitle({ children }) {
  return (
    <GridItem colSpan={{ base: 1, md: 2 }} pt={2}>
      <Text fontSize="12px" fontWeight="600" letterSpacing=".06em" textTransform="uppercase" color="gray.500">{children}</Text>
    </GridItem>
  );
}

function Field({ id, label, full, children, help }) {
  return (
    <GridItem colSpan={{ base: 1, md: full ? 2 : 1 }}>
      <FormControl>
        <FormLabel htmlFor={id} fontSize="13px" color="gray.600" mb={1}>{label}</FormLabel>
        {children}
        {help && <Text fontSize="12px" color="gray.500" mt={1}>{help}</Text>}
      </FormControl>
    </GridItem>
  );
}

const selectProps = { size: 'sm', borderRadius: '8px', bg: 'white' };
const inputProps = { size: 'sm', borderRadius: '8px', bg: 'white' };

export default function RequestFields({ values, onChange, options, idPrefix = 'dr', readOnly = false }) {
  const set = (name) => (e) => onChange(name, e.target.value);
  const opts = options || {};
  const types = opts.types || Object.keys(TYPE_LABEL);
  const kinds = opts.constancyKinds || Object.keys(KIND_LABEL);
  const statuses = opts.statuses || Object.keys(STATUS);
  const channels = opts.channels || Object.keys(CHANNEL_LABEL);
  const priorities = opts.priorities || Object.keys(PRIORITY);
  const payments = opts.paymentStatuses || Object.keys(PAYMENT);
  const deliveryMethods = opts.deliveryMethods || Object.keys(DELIVERY_LABEL);
  const assignees = opts.assignees || [];
  const needsReason = STATUSES_WITH_REASON.includes(values.status);
  const id = (name) => `${idPrefix}-${name}`;

  return (
    <Box as="fieldset" disabled={readOnly}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={3}>
        <Field id={id('type')} label="Tipo de documento">
          <Select id={id('type')} {...selectProps} value={values.type} onChange={set('type')}>
            {types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
          </Select>
        </Field>
        {values.type === 'constancia' ? (
          <Field id={id('kind')} label="Tipo de constancia">
            <Select id={id('kind')} {...selectProps} value={values.constancyKind} onChange={set('constancyKind')}>
              <option value="">Sin definir</option>
              {kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
            </Select>
          </Field>
        ) : <GridItem display={{ base: 'none', md: 'block' }} />}

        <Field id={id('status')} label="Estado">
          <Select id={id('status')} {...selectProps} value={values.status} onChange={set('status')}>
            {statuses.map((s) => <option key={s} value={s}>{STATUS[s]?.label || s}</option>)}
          </Select>
        </Field>
        <Field id={id('priority')} label="Prioridad">
          <Select id={id('priority')} {...selectProps} value={values.priority} onChange={set('priority')}>
            {priorities.map((p) => <option key={p} value={p}>{PRIORITY[p]?.label || p}</option>)}
          </Select>
        </Field>
        {needsReason && (
          <Field id={id('reason')} label="Motivo" full help="Obligatorio: es lo que se le explica a la persona.">
            <Textarea
              id={id('reason')} size="sm" borderRadius="8px" rows={2} value={values.statusReason} onChange={set('statusReason')}
              placeholder={values.status === 'no_cumple' ? 'Ej. Tiene requisitos pendientes por aprobar' : 'Ej. No pagó en 5 días'}
            />
          </Field>
        )}

        <Field id={id('channel')} label="Canal de ingreso">
          <Select id={id('channel')} {...selectProps} value={values.channel} onChange={set('channel')}>
            {channels.map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c] || c}</option>)}
          </Select>
        </Field>
        <Field id={id('assigned')} label="Responsable">
          <Select id={id('assigned')} {...selectProps} value={values.assignedTo} onChange={set('assignedTo')}>
            <option value="">Sin asignar</option>
            {assignees.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
          </Select>
        </Field>

        <Field id={id('requested')} label="Fecha de solicitud">
          <Input id={id('requested')} type="date" {...inputProps} value={values.requestedAt} onChange={set('requestedAt')} />
        </Field>
        <Field id={id('delivered')} label="Fecha de entrega">
          <Input id={id('delivered')} type="date" {...inputProps} value={values.deliveredAt} onChange={set('deliveredAt')} />
        </Field>

        <Field id={id('obs')} label="Especificaciones / observaciones" full>
          <Textarea
            id={id('obs')} size="sm" borderRadius="8px" rows={2} value={values.observations} onChange={set('observations')}
            placeholder="Ej. CLEI VI, para la caja de compensación"
          />
        </Field>

        <SectionTitle>Contacto</SectionTitle>
        <Field id={id('phone')} label="Celular">
          <Input id={id('phone')} {...inputProps} inputMode="tel" value={values.phone} onChange={set('phone')} placeholder="Ej. 3001234567" />
        </Field>
        <Field id={id('email')} label="Correo">
          <Input id={id('email')} type="email" {...inputProps} value={values.email} onChange={set('email')} placeholder="nombre@correo.com" />
        </Field>

        <SectionTitle>Entrega</SectionTitle>
        <Field id={id('delivery')} label="Método de envío">
          <Select id={id('delivery')} {...selectProps} value={values.deliveryMethod} onChange={set('deliveryMethod')}>
            {deliveryMethods.map((m) => <option key={m} value={m}>{DELIVERY_LABEL[m] || m}</option>)}
          </Select>
        </Field>
        <GridItem display={{ base: 'none', md: 'block' }} />
        {values.deliveryMethod === 'fisico' && (
          <>
            <Field id={id('address')} label="Dirección" full>
              <Input id={id('address')} {...inputProps} value={values.deliveryAddress} onChange={set('deliveryAddress')} />
            </Field>
            <Field id={id('neighborhood')} label="Barrio">
              <Input id={id('neighborhood')} {...inputProps} value={values.deliveryNeighborhood} onChange={set('deliveryNeighborhood')} />
            </Field>
            <Field id={id('city')} label="Ciudad">
              <Input id={id('city')} {...inputProps} value={values.deliveryCity} onChange={set('deliveryCity')} />
            </Field>
            <Field id={id('department')} label="Departamento">
              <Input id={id('department')} {...inputProps} value={values.deliveryDepartment} onChange={set('deliveryDepartment')} />
            </Field>
            <Field id={id('dphone')} label="Teléfono de quien recibe">
              <Input id={id('dphone')} {...inputProps} inputMode="tel" value={values.deliveryPhone} onChange={set('deliveryPhone')} />
            </Field>
          </>
        )}

        <SectionTitle>Pago</SectionTitle>
        <Field id={id('amount')} label="Valor (COP)">
          <Input id={id('amount')} {...inputProps} inputMode="numeric" value={values.amount} onChange={set('amount')} placeholder="Ej. 25000" />
        </Field>
        <Field id={id('payment')} label="Estado del pago">
          <Select id={id('payment')} {...selectProps} value={values.paymentStatus} onChange={set('paymentStatus')}>
            {payments.map((p) => <option key={p} value={p}>{PAYMENT[p]?.label || p}</option>)}
          </Select>
        </Field>

        <Field id={id('comments')} label="Comentarios internos" full>
          <Textarea id={id('comments')} size="sm" borderRadius="8px" rows={2} value={values.comments} onChange={set('comments')} />
        </Field>
      </Grid>
    </Box>
  );
}
